// src/components/fuel/CompetitorPricesWidget.js
import React, { useState, useEffect } from 'react';
import { TrendingUp, MapPin, Navigation, AlertCircle, Fuel as FuelIcon } from 'lucide-react';
import { API_BASE, FUEL_META } from './constants';
import { fmtUSD } from './formatters';
import axios from 'axios';

const CompetitorPricesWidget = ({ airportCoords, fuelType, onFuelTypeChange, currentPrice }) => {
  const [stats, setStats] = useState(null);
  const [nearby, setNearby] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (airportCoords && fuelType) {
      fetchData();
    }
  }, [airportCoords, fuelType]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsRes, nearbyRes] = await Promise.all([
        axios.get(`${API_BASE}/competitor/stats/${fuelType}`),
        axios.get(`${API_BASE}/competitor/nearby/${fuelType}?lat=${airportCoords.lat}&lon=${airportCoords.lon}&radius_miles=150`)
      ]);
      setStats(statsRes.data);
      setNearby(nearbyRes.data || []);
    } catch (err) {
      console.error('Failed to load competitor prices', err);
      setError('Unable to load market data');
    } finally {
      setLoading(false);
    }
  };

  const fuelMeta = FUEL_META[fuelType] || FUEL_META['JetA'];
  const comparison = currentPrice && stats?.avg_price ? currentPrice - stats.avg_price : null;
  const isAboveAvg = comparison > 0;
  const isBelowAvg = comparison < 0;

  return (
    <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-4">
      {/* Header avec sélecteur */}
      <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
        <h3 className="text-white font-semibold flex items-center gap-2 text-sm">
          <TrendingUp size={16} className="text-purple-400" />
          Market Comparison
        </h3>
        <div className="flex items-center gap-1 bg-gray-700 rounded-lg p-0.5">
          {Object.entries(FUEL_META).map(([key, meta]) => (
            <button
              key={key}
              onClick={() => onFuelTypeChange(key)}
              className={`px-2 py-1 rounded-md text-[10px] sm:text-xs transition ${
                fuelType === key 
                  ? `${meta.badge} font-semibold` 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              {meta.label === 'Jet-A' ? 'JetA' : meta.label === '100LL Avgas' ? '100LL' : 'JetA+'}
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-6">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-500" />
        </div>
      )}

      {error && (
        <div className="text-center py-4">
          <AlertCircle size={20} className="text-yellow-400 mx-auto mb-1" />
          <p className="text-gray-400 text-[10px]">{error}</p>
        </div>
      )}

      {!loading && !error && stats && (
        <>
          {/* Stats min/avg/max - version compacte */}
          <div className="grid grid-cols-3 gap-1 mb-3 text-center">
            <div className="bg-gray-700/30 rounded-lg py-1.5 px-1">
              <p className="text-gray-500 text-[10px]">Min</p>
              <p className="text-green-400 font-bold text-sm">${stats.min_price?.toFixed(3) || '—'}</p>
            </div>
            <div className="bg-gray-700/30 rounded-lg py-1.5 px-1">
              <p className="text-gray-500 text-[10px]">Avg</p>
              <p className="text-yellow-400 font-bold text-sm">${stats.avg_price?.toFixed(3) || '—'}</p>
            </div>
            <div className="bg-gray-700/30 rounded-lg py-1.5 px-1">
              <p className="text-gray-500 text-[10px]">Max</p>
              <p className="text-red-400 font-bold text-sm">${stats.max_price?.toFixed(3) || '—'}</p>
            </div>
          </div>

          {/* Your price - version compacte */}
          {currentPrice && (
            <div className={`rounded-lg py-2 px-2 mb-3 text-center ${
              isAboveAvg ? 'bg-red-500/10 border border-red-500/30' : 
              isBelowAvg ? 'bg-green-500/10 border border-green-500/30' : 
              'bg-gray-700/30'
            }`}>
              <p className="text-gray-400 text-[10px]">Your price</p>
              <p className="text-white font-bold text-base">{fmtUSD(currentPrice)}/gal</p>
              {comparison !== 0 && (
                <p className={`text-[10px] ${isAboveAvg ? 'text-red-400' : 'text-green-400'}`}>
                  {isAboveAvg ? `↑ ${Math.abs(comparison).toFixed(3)} above avg` : 
                   isBelowAvg ? `↓ ${Math.abs(comparison).toFixed(3)} below avg` : ''}
                </p>
              )}
            </div>
          )}

          {/* Nearby airports - version compacte */}
          <div>
            <p className="text-gray-500 text-[10px] mb-2 flex items-center gap-1">
              <MapPin size={10} /> Nearby airports (150 mi)
            </p>
            <div className="space-y-1.5 max-h-32 overflow-y-auto">
              {nearby.length === 0 ? (
                <p className="text-gray-500 text-[10px] text-center py-2">No competitor data</p>
              ) : (
                nearby.slice(0, 4).map(airport => (
                  <div key={airport.airport_code} className="flex justify-between items-center text-[10px] py-1 border-b border-gray-700">
                    <div className="flex-1">
                      <span className="text-white font-mono text-[11px]">{airport.airport_code}</span>
                    </div>
                    <span className="text-gray-400 w-12 text-right">
                      {airport.distance_miles ? `${Math.round(airport.distance_miles)}mi` : '—'}
                    </span>
                    <span className="font-mono text-white w-16 text-right">${airport.price_per_gallon}</span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Last updated */}
          {nearby.length > 0 && nearby[0]?.last_updated && (
            <p className="text-gray-500 text-[9px] text-right mt-2">
              Updated: {new Date(nearby[0].last_updated).toLocaleDateString()}
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default CompetitorPricesWidget;